import type { MantineSize } from '@mantine/core';
import { Global } from '@mantine/emotion';

/**
 * Calculate Mantine spacing with multiplier
 * @param size - Mantine spacing size (xxs, xs, sm, md, lg, xl, 2xl)
 * @param multiplier - Multiplier for the spacing (e.g., 0.5 for half, 2 for double, 3 for triple)
 * @returns CSS calc expression for the spacing
 */
export function calcSpacing(
  size: MantineSize | 'xxs' | '2xl',
  multiplier = 1.0,
): string {
  // Handle custom sizes (Mantine v7 doesn't support custom spacing sizes in theme)
  // We can delete this once we upgrade to Mantine v8 and can define our own xxl / 2xl in the theme
  const customSizes: Record<string, string> = {
    xxs: '0.5rem',
    '2xl': '2rem',
  };

  if (size in customSizes) {
    const baseValue = customSizes[size];
    if (multiplier === 1) {
      return baseValue;
    }
    if (multiplier < 1) {
      return `calc(${baseValue} / ${1 / multiplier})`;
    }
    return `calc(${baseValue} * ${multiplier})`;
  }

  // Handle standard Mantine sizes
  if (multiplier === 1) {
    return `var(--mantine-spacing-${size})`;
  }
  if (multiplier < 1) {
    return `calc(var(--mantine-spacing-${size}) / ${1 / multiplier})`;
  }
  return `calc(var(--mantine-spacing-${size}) * ${multiplier})`;
}

export const GlobalStyles = () => {
  return (
    <Global
      styles={() => ({
        html: {
          height: '100vh',
          '@supports (height: 100dvh)': {
            height: '100dvh',
          },
        },
        'body, #root, #storybook-root': {
          height: '100%',
        },
      })}
    />
  );
};
